import { z } from 'zod';

import type { ArquivoResposta } from '../shared/arquivo';
import type { ErrorCode } from '../shared/errors';

/** Teto de linhas por resposta: o `max_rows` do PostgREST de produção (T01 §3.6.5, R-15). */
export const MAX_ROWS = 1000;

/** Paginação de toda listagem: espalhe em `query` (`z.object({ ...page, ...filtros })`). */
export const page = {
  limit: z.coerce.number().int().min(1).max(MAX_ROWS).default(MAX_ROWS),
  offset: z.coerce.number().int().min(0).default(0),
};

/** Saída de endpoint novo de lista. `total` só onde a tela já pagina por número de página. */
export const listOutput = <I extends z.ZodType, E extends z.ZodRawShape = Record<never, never>>(item: I, extras?: E) =>
  z.object({
    items: z.array(item),
    limit: z.number().int(),
    offset: z.number().int(),
    total: z.number().int().optional(),
    ...(extras ?? ({} as E)),
  });

/**
 * `timestamptz` no contrato HTTP: `Date` no servidor e no cliente, ISO UTC (`…Z`) no fio.
 * Codec porque o serializador do Fastify codifica (`encode`) a saída e o cliente a decodifica.
 */
export const instante = z.codec(z.iso.datetime({ offset: true }), z.date(), {
  decode: (iso) => new Date(iso),
  encode: (date) => date.toISOString(),
});

/** `numeric(precisao, escala)` positivo: recusa no Zod o que estouraria a coluna (22003 ⇒ 500). */
export const decimalPositivo = (precisao: number, escala: number) =>
  z.number().positive().lt(10 ** (precisao - escala));

/** `output` de endpoint que devolve bytes (rota com `{ arquivo: true }`): sem JSON nem envelope. */
export const arquivoResposta = z.custom<ArquivoResposta>((v) => typeof v === 'object' && v !== null && 'conteudo' in v);

export type ApiSuccess<T> = { success: true; data: T };
export type ApiFailure = { success: false; error: string; code: ErrorCode };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

// TODO(onda 2): src/lib/utils/constants.ts (ESTADOS_BR) passa a reexportar daqui (mover, não copiar).
export const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

export const Uf = z.enum(ESTADOS_BR);
export type Uf = z.infer<typeof Uf>;
